import { enableProdMode, provideZoneChangeDetection, ErrorHandler, importProvidersFrom } from '@angular/core';

import { HttpLoaderFactory } from './app/app.module';
import {environment} from './environments/environment';
import { providePrimeNG } from 'primeng/config';
import { definePreset, palette, ColorScale } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { AuthService } from '@service/auth.service';
import { DatePipe } from '@angular/common';
import { UserService } from '@service/user.service';
import { NavigationService } from '@service/navigation.service';
import { AuthGuard } from '@guard/auth.guard';
import { LoaderService } from '@service/loader.service';
import { ProfileService } from '@service/profile.service';
import { GeoPlaceService } from '@service/geoplace.service';
import { GeoCountyService } from '@service/geocounty.service';
import { GeoDistrictService } from '@service/geodistrict.service';
import { GroupService } from '@service/group.service';
import { AddressService } from '@service/address.service';
import { UrbisService } from '@service/urbis.service';
import { NotificationService } from '@service/notification.service';
import { UserNotificationService } from '@service/user-notification.service';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { AuthInterceptor } from '@interceptor/auth.interceptor';
import { EnumInterceptor } from '@interceptor/enum.interceptor';
import { LoaderInterceptor } from '@interceptor/loader.interceptor';
import { GlobalErrorHandler } from '@util/GlobalErrorHandler';
import { MessageService, ConfirmationService } from 'primeng/api';
import { GeoPostalCodeService } from '@service/geopostal-code.service';
import { GeocountryService } from '@service/geocountry.service';
import { AssetsService } from '@service/assets.service';
import { ChallengeService } from '@service/challenge.service';
import { PrizeService } from '@service/prize.service';
import { AuctionService } from '@service/auction.service';
import { OffersService } from '@service/offers.service';

import { ModulesModule } from '@modules/modules.module';

import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SplitterModule } from 'primeng/splitter';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app/app.component';

const AUD_PRESET = definePreset(Aura, {
    semantic: {
        // O cast e da biblioteca, nao nosso: o `palette()` do @primeuix/styled
        // declara `string | ColorScale` porque devolve a string tal e qual
        // quando recebe uma referencia a token (`'{blue}'`). Com uma cor
        // literal devolve sempre a escala, e `semantic.primary` exige
        // `ColorScale`. Deixou de compilar no primeng 20, que apertou o tipo.
        primary: palette('#2196F3') as ColorScale,
    },
    components: {
        // Toasts: repor o aspeto do saga-blue.
        //
        // O Aura desenha-os "leves" -- fundo quase branco (`red.50` a 95% de
        // transparencia), borda fina a toda a volta e so o icone colorido. O
        // saga-blue desenhava-os "preenchidos": fundo saturado, texto escuro da
        // mesma familia e uma barra de 6px do lado esquerdo. Sao duas linguagens
        // visuais diferentes, e a segunda e a que a aplicacao tinha.
        //
        // Os valores sao os do proprio saga-blue, tirados do
        // `resources/themes/saga-blue/theme.css` do primeng 17 e nao inventados.
        //
        // NOTA: nao confundir isto com o bloco `.ui-toast-message` que existia
        // em `styles.scss`. Esse usava o naming do primeng 5/6 e estava morto ha
        // muitos majors -- foi removido neste hop. O aspeto antigo vinha todo do
        // tema, nunca do projeto.
        // Accordion: repor o aspeto do saga-blue.
        //
        // O Aura desenha-o liso -- cabecalho com fundo branco, sem borda, e os
        // paineis separados por uma linha em baixo. O saga-blue punha o
        // cabecalho numa barra cinzenta com borda a toda a volta.
        //
        // Valores tirados do `saga-blue/theme.css` do primeng 17. O que nao se
        // resolve por token e a POSICAO do chevron: no componente novo ele vem
        // depois do texto no DOM, e no antigo vinha antes. Isso esta em
        // styles.scss, com um `flex-direction: row-reverse`.
        accordion: {
            panel: {
                // Cada painel leva a sua propria borda pelo cabecalho e pelo
                // conteudo, como no saga-blue. Sem isto ficava uma linha a mais.
                borderWidth: '0',
            },
            header: {
                padding: '1rem',
                borderWidth: '1px',
                borderColor: '#dee2e6',
                borderRadius: '3px',
                color: '#495057',
                hoverColor: '#495057',
                activeColor: '#495057',
                background: '#f8f9fa',
                hoverBackground: '#f8f9fa',
                activeBackground: '#f8f9fa',
                activeHoverBackground: '#f8f9fa',
                toggleIcon: {
                    color: '#495057',
                    hoverColor: '#495057',
                    activeColor: '#495057',
                    activeHoverColor: '#495057',
                },
            },
            content: {
                padding: '1rem',
                borderWidth: '0 1px 1px 1px',
                borderColor: '#dee2e6',
                background: '#ffffff',
                color: '#495057',
            },
        },
        toast: {
            root: {
                // O saga-blue punha a borda so a esquerda, com 6px.
                borderWidth: '0 0 0 6px',
            },
            colorScheme: {
                light: {
                    info: {
                        background: '#b3e5fc',
                        borderColor: '#0891cf',
                        color: '#044868',
                        detailColor: '#044868',
                    },
                    success: {
                        background: '#c8e6c9',
                        borderColor: '#439446',
                        color: '#224a23',
                        detailColor: '#224a23',
                    },
                    warn: {
                        background: '#ffecb3',
                        borderColor: '#d9a300',
                        color: '#6d5100',
                        detailColor: '#6d5100',
                    },
                    error: {
                        background: '#ffcdd2',
                        borderColor: '#e60017',
                        color: '#73000c',
                        detailColor: '#73000c',
                    },
                },
            },
        },
    },
});



if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        importProvidersFrom(
            HttpClientModule,
            BrowserModule,
            BrowserAnimationsModule,
            AppRoutingModule,
            ModulesModule,
            NgbModule,
            SplitterModule,
            ReactiveFormsModule.withConfig({callSetDisabledState: 'whenDisabledForLegacyCode'}),
            TranslateModule.forRoot({
                defaultLanguage: 'pt',
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [ HttpClient ]
                }
            })
        ),
        // Hop 19-PN-TEMA. O primeng 18 deixou de publicar `primeng/resources`:
        // o tema saga-blue e o primeng.min.css deixaram de existir como
        // ficheiros e sairam do array `styles` do angular.json. O estilo passa
        // a vir de um preset de design tokens, injetado aqui.
        //
        // `darkModeSelector: false` e deliberado: o projeto nao tem modo
        // escuro e o omisso do Aura reage a `prefers-color-scheme`, o que
        // introduziria uma regressao visual que ninguem pediu.
        providePrimeNG({
            theme: {
                preset: AUD_PRESET,
                options: { darkModeSelector: false },
            },
        }),
        AuthService,
        DatePipe,
        UserService,
        NavigationService,
        AuthGuard,
        LoaderService,
        ProfileService,
        GeoPlaceService,
        GeoCountyService,
        GeoDistrictService,
        GroupService,
        AddressService,
        UrbisService,
        NotificationService,
        UserNotificationService,
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: EnumInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        MessageService,
        ConfirmationService,
        GeoPostalCodeService,
        GeocountryService,
        AssetsService,
        ChallengeService,
        PrizeService,
        AuctionService,
        OffersService
    ]
})
    .catch(err => console.error(err));
